import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, StatusPill, EmptyState } from "@/components/Bits";
import { dmy, titleize } from "@/lib/format";

export default function PlatformUsers() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/users");
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    try {
      await api.post(`/owner/users/${u.user_id}/status`, { status: next });
      toast.success(`Account ${next}.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} />;
  if (!items) return <Loading label="Loading platform users…" />;

  const filtered = items.filter((u) =>
    !q || `${u.name} ${u.email} ${u.business_name || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="platform-users-page">
      <PageHeader overline="Access control" title="Platform users"
        subtitle="Every account across the platform. Passwords are never visible — use a secure reset instead." />
      <input placeholder="Search name, email or business" value={q} onChange={(e) => setQ(e.target.value)}
        data-testid="users-search-input"
        className="mb-6 w-full sm:w-96 rounded-lg border border-slate-300 px-4 py-2 text-sm outline-none focus:border-slate-900" />
      <div className="panel overflow-hidden">
        {filtered.length === 0 ? <EmptyState title="No users found" testid="users-empty" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-4 font-semibold">Name</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Role</th>
                  <th className="px-6 py-4 font-semibold">Business</th>
                  <th className="px-6 py-4 font-semibold">Created</th>
                  <th className="px-6 py-4 font-semibold">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-4 font-medium text-slate-900">{u.name}</td>
                    <td className="px-6 py-4 text-slate-600">{u.email}</td>
                    <td className="px-6 py-4 text-slate-600">{titleize(u.role)}</td>
                    <td className="px-6 py-4 text-slate-600">{u.business_name || "—"}</td>
                    <td className="px-6 py-4 text-slate-500">{dmy(u.created_at)}</td>
                    <td className="px-6 py-4"><StatusPill status={u.status} /></td>
                    <td className="px-6 py-4 text-right">
                      {u.role !== "platform_owner" && (
                        <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.user_id}`}
                          className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900">
                          {u.status === "active" ? "Suspend" : "Activate"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
